import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import TopAppBar from '../components/TopAppBar';
import BottomNavBar from '../components/BottomNavBar';
import Footer from '../components/Footer';
import { submitContactForm } from '../api/contact';

declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement, options: {
        sitekey: string;
        callback: (token: string) => void;
        'expired-callback': () => void;
        'error-callback': () => void;
        theme?: 'light' | 'dark' | 'auto';
      }) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY ?? '';

const ContactPage: React.FC = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [token, setToken] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const widgetRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);

  const renderWidget = useCallback(() => {
    if (!widgetRef.current || !window.turnstile || !SITE_KEY) return;
    if (widgetIdRef.current) return; // already rendered

    widgetIdRef.current = window.turnstile.render(widgetRef.current, {
      sitekey: SITE_KEY,
      callback: (t) => setToken(t),
      'expired-callback': () => setToken(null),
      'error-callback': () => setToken(null),
      theme: 'light',
    });
  }, []);

  useEffect(() => {
    // Turnstile script may already be loaded or still loading
    if (window.turnstile) {
      renderWidget();
    } else {
      const interval = setInterval(() => {
        if (window.turnstile) {
          clearInterval(interval);
          renderWidget();
        }
      }, 100);
      return () => clearInterval(interval);
    }
  }, [renderWidget]);

  useEffect(() => {
    return () => {
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, []);

  const resetWidget = () => {
    if (widgetIdRef.current && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
    }
    setToken(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) {
      setError('Please complete the CAPTCHA.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await submitContactForm({ name, email, message, turnstileToken: token });
      setSubmitted(true);
    } catch {
      setError('Something went wrong. Please try again.');
      resetWidget();
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = 'w-full px-4 py-3 border border-outline-variant rounded-lg text-sm bg-surface text-on-surface focus:outline-none focus:ring-2 focus:ring-primary';
  const labelClass = 'block text-xs font-semibold text-on-surface-variant uppercase tracking-wider mb-1.5';

  return (
    <div className="bg-background text-on-background min-h-screen pb-24 md:pb-0">
      <TopAppBar />
      <main className="max-w-2xl mx-auto px-container-margin py-10 md:py-16">
        <div className="flex items-center gap-3 mb-8">
          <Link to="/" className="text-primary hover:underline text-sm">Home</Link>
          <span className="text-outline">/</span>
          <h1 className="text-3xl font-bold text-on-surface">Contact us</h1>
        </div>

        {submitted ? (
          <div className="bg-surface border border-outline-variant rounded-2xl p-8 text-center">
            <span className="material-symbols-outlined text-primary text-5xl mb-4 block" style={{ fontVariationSettings: "'FILL' 1" }}>
              check_circle
            </span>
            <h2 className="text-xl font-semibold text-on-surface mb-2">Message sent</h2>
            <p className="text-on-surface-variant text-sm">
              We'll get back to you at <strong>{email}</strong> as soon as possible.
            </p>
          </div>
        ) : (
          <div className="bg-surface border border-outline-variant rounded-2xl p-6 md:p-8">
            <p className="text-on-surface-variant text-sm mb-6">
              Have a question or need help? Fill in the form and we'll get back to you.
            </p>

            {error && (
              <p className="text-error text-sm mb-4">{error}</p>
            )}

            <form onSubmit={handleSubmit} className="flex flex-col gap-5">
              <div>
                <label className={labelClass} htmlFor="name">Name</label>
                <input
                  id="name"
                  type="text"
                  required
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="Your name"
                  className={inputClass}
                />
              </div>

              <div>
                <label className={labelClass} htmlFor="email">Email</label>
                <input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className={inputClass}
                />
              </div>

              <div>
                <label className={labelClass} htmlFor="message">Message</label>
                <textarea
                  id="message"
                  required
                  rows={6}
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  placeholder="How can we help?"
                  className={`${inputClass} resize-none`}
                />
              </div>

              {/* Turnstile widget */}
              <div ref={widgetRef} />

              <button
                type="submit"
                disabled={submitting || !token}
                className="px-6 py-3 bg-primary text-on-primary rounded-lg font-semibold text-sm hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Sending...' : 'Send message'}
              </button>
            </form>
          </div>
        )}
      </main>
      <Footer />
      <BottomNavBar />
    </div>
  );
};

export default ContactPage;
